/**
 * CollectionGroupManagement - Owner dashboard for managing collection groups
 * Elvis Aloha Collection Archive
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Star, GripVertical, 
  Package, Layers, BookOpen, Archive, Image, ChevronRight,
  Search, Filter, MoreVertical, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fetchWithAuth = async (url, options = {}) => {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

const GROUP_TYPE_CONFIG = {
  curated: { label: 'Curated', icon: Layers, color: 'text-amber-700', bg: 'bg-amber-50' },
  exhibition: { label: 'Exhibition', icon: BookOpen, color: 'text-blue-700', bg: 'bg-blue-50' },
  category: { label: 'Category', icon: Package, color: 'text-stone-600', bg: 'bg-stone-50' },
  research: { label: 'Research', icon: Archive, color: 'text-emerald-700', bg: 'bg-emerald-50' },
  acquisition: { label: 'Acquisition', icon: Archive, color: 'text-stone-500', bg: 'bg-stone-50' },
};

const VISIBILITY_CONFIG = {
  public: { label: 'Public', color: 'text-green-700', bg: 'bg-green-50' },
  private: { label: 'Private', color: 'text-stone-600', bg: 'bg-stone-100' },
  unlisted: { label: 'Unlisted', color: 'text-amber-700', bg: 'bg-amber-50' },
};

function CollectionGroupRow({ group, onDelete, onToggleFeatured }) {
  const [deleting, setDeleting] = useState(false);
  const typeConfig = GROUP_TYPE_CONFIG[group.group_type] || GROUP_TYPE_CONFIG.curated;
  const visConfig = VISIBILITY_CONFIG[group.visibility] || VISIBILITY_CONFIG.public;
  const TypeIcon = typeConfig.icon;
  
  const handleDelete = async () => {
    if (!window.confirm(`Delete "${group.name}"? This cannot be undone.`)) return;
    
    setDeleting(true);
    try {
      await fetchWithAuth(`/api/collection-groups/manage/${group.id}`, { method: 'DELETE' });
      onDelete(group.id);
      toast.success('Collection group deleted');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <tr className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`group-row-${group.id}`}>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-stone-400 cursor-grab" />
          <span className="text-sm text-stone-500">{group.display_order}</span>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          {group.thumbnail_url || group.hero_image_url ? (
            <img 
              src={group.thumbnail_url || group.hero_image_url}
              alt=""
              className="w-12 h-12 rounded-lg object-cover border border-stone-200"
            />
          ) : (
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${typeConfig.bg}`}>
              <TypeIcon className={`w-5 h-5 ${typeConfig.color}`} />
            </div>
          )}
          <div>
            <div className="font-medium text-stone-900">{group.name}</div>
            <div className="text-xs text-stone-500">{group.slug}</div>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${typeConfig.bg} ${typeConfig.color}`}>
          <TypeIcon className="w-3 h-3" />
          {typeConfig.label}
        </span>
      </td>
      <td className="py-4 px-4">
        <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full ${visConfig.bg} ${visConfig.color}`}>
          {group.visibility === 'public' ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
          {visConfig.label}
        </span>
      </td>
      <td className="py-4 px-4 text-center">
        <span className="text-sm text-stone-700">{group.artifact_count || 0}</span>
      </td>
      <td className="py-4 px-4">
        <button
          onClick={() => onToggleFeatured(group.id, !group.is_featured)}
          className={`p-1.5 rounded-lg transition-colors ${
            group.is_featured 
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
              : 'bg-stone-100 text-stone-400 hover:text-amber-600 hover:bg-amber-50'
          }`}
          title={group.is_featured ? 'Remove from featured' : 'Add to featured'}
        >
          <Star className={`w-4 h-4 ${group.is_featured ? 'fill-current' : ''}`} />
        </button>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <Link
            to={`/manage/collections/${group.id}`}
            className="p-2 text-stone-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </Link>
          <a
            href={`/collection/groups/${group.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-stone-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="View public page"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-2 text-stone-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function CollectionGroupManagement() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ type: '', visibility: '' });
  
  const fetchGroups = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.type) params.append('group_type', filter.type);
      if (filter.visibility) params.append('visibility', filter.visibility);
      
      const data = await fetchWithAuth(`/api/collection-groups/manage?${params.toString()}`);
      setGroups(data.groups || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);
  
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);
  
  const handleDelete = (groupId) => {
    setGroups(prev => prev.filter(g => g.id !== groupId));
  };
  
  const handleToggleFeatured = async (groupId, featured) => {
    try {
      await fetchWithAuth(`/api/collection-groups/manage/${groupId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_featured: featured })
      });
      
      setGroups(prev => prev.map(g => 
        g.id === groupId ? { ...g, is_featured: featured } : g
      ));
      toast.success(featured ? 'Added to featured' : 'Removed from featured');
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const filteredGroups = groups.filter(g => 
    g.name.toLowerCase().includes(search.toLowerCase()) ||
    g.slug.includes(search.toLowerCase())
  );
  
  return (
    <div className="p-6" data-testid="collection-group-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Collection Groups</h1>
          <p className="text-stone-600 mt-1">
            Organize artifacts into thematic collections for public display
          </p>
        </div>
        <Link to="/manage/collections/new">
          <Button className="bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="w-4 h-4 mr-2" />
            New Collection
          </Button>
        </Link>
      </div>
      
      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 bg-white p-4 rounded-xl border border-stone-200">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search collections..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        
        <select
          value={filter.type}
          onChange={(e) => setFilter(prev => ({ ...prev, type: e.target.value }))}
          className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        >
          <option value="">All Types</option>
          <option value="curated">Curated</option>
          <option value="exhibition">Exhibition</option>
          <option value="category">Category</option>
          <option value="research">Research</option>
          <option value="acquisition">Acquisition</option>
        </select>
        
        <select
          value={filter.visibility}
          onChange={(e) => setFilter(prev => ({ ...prev, visibility: e.target.value }))}
          className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        >
          <option value="">All Visibility</option>
          <option value="public">Public</option>
          <option value="private">Private</option>
          <option value="unlisted">Unlisted</option>
        </select>
      </div>
      
      {/* Table */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700" />
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-16">
            <Layers className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500 mb-4">
              {search || filter.type || filter.visibility
                ? 'No collections match your filters'
                : 'No collection groups yet'}
            </p>
            <Link to="/manage/collections/new">
              <Button variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create First Collection
              </Button>
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left text-xs font-medium text-stone-500 uppercase tracking-wider py-3 px-4 w-16">
                  Order
                </th>
                <th className="text-left text-xs font-medium text-stone-500 uppercase tracking-wider py-3 px-4">
                  Collection
                </th>
                <th className="text-left text-xs font-medium text-stone-500 uppercase tracking-wider py-3 px-4 w-28">
                  Type
                </th>
                <th className="text-left text-xs font-medium text-stone-500 uppercase tracking-wider py-3 px-4 w-28">
                  Visibility
                </th>
                <th className="text-center text-xs font-medium text-stone-500 uppercase tracking-wider py-3 px-4 w-20">
                  Items
                </th>
                <th className="text-left text-xs font-medium text-stone-500 uppercase tracking-wider py-3 px-4 w-16">
                  Featured
                </th>
                <th className="text-left text-xs font-medium text-stone-500 uppercase tracking-wider py-3 px-4 w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map(group => (
                <CollectionGroupRow
                  key={group.id}
                  group={group}
                  onDelete={handleDelete}
                  onToggleFeatured={handleToggleFeatured}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Stats */}
      {groups.length > 0 && (
        <div className="mt-4 text-sm text-stone-500">
          {groups.length} collection{groups.length !== 1 ? 's' : ''} • 
          {groups.filter(g => g.is_featured).length} featured • 
          {groups.filter(g => g.visibility === 'public').length} public
        </div>
      )}
    </div>
  );
}
